/**
 * Zustand auth store — persists tokens to localStorage and holds the
 * admin profile (id, roles, menus, nodes).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AdminProfile {
  id: number;
  username: string;
  roles: { id: number; name: string; preset: string | null }[];
  is_super: boolean;
  menus: string[];
  nodes: string[];
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  profile: AdminProfile | null;
  setTokens: (access: string, refresh: string) => void;
  setProfile: (p: AdminProfile) => void;
  clear: () => void;
  hasNode: (key: string) => boolean;
  hasMenu: (key: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      profile: null,
      setTokens: (access, refresh) => set({ accessToken: access, refreshToken: refresh }),
      setProfile: (p) => set({ profile: p }),
      clear: () => set({ accessToken: null, refreshToken: null, profile: null }),
      hasNode: (key: string) => {
        const p = get().profile;
        if (!p) return false;
        if (p.is_super || p.nodes.includes('*')) return true;
        return p.nodes.includes(key);
      },
      hasMenu: (key: string) => {
        const p = get().profile;
        if (!p) return false;
        if (p.is_super || p.menus.includes('*')) return true;
        return p.menus.includes(key);
      },
    }),
    { name: 'uo-admin-auth' },
  ),
);
